/**
 * PredictiveSearch
 * Standard Shopify Predictive Search mit mehrstufigem Fallback für Typos,
 * Umlaut-Schreibweisen und Compound-Token (z.B. "se:hive", "x-1000").
 *
 * Verhalten bei einer Suche:
 *   1. Anfrage wird unverändert an Shopify gestellt (Cache pro URL).
 *   2. Bei 0 Treffern: Deutsche Transliteration (ue->ü, oe->ö, ae->ä, ss->ß)
 *      und erneuter Versuch.
 *   3. Bei immer noch 0 Treffern: Compound-Expansion. Jeder Query-Begriff der
 *      im Produkt-Index als Atom eines Compounds vorkommt (z.B. "hive" in
 *      "se:hive") wird durch das volle Compound ersetzt und nochmals an
 *      Shopify geschickt – behebt Shopifys Tokenizer-Schwäche bei ":", "-",
 *      "_", "/" und ".".
 *   4. Bei immer noch 0 Treffern: jeder Suchbegriff wird gegen ein lazy
 *      geladenes Produkt-Vokabular per Damerau-Levenshtein korrigiert.
 *      Erneuter Versuch mit der korrigierten Phrase.
 *   5. Wenn nichts funktioniert: Original-Empty-State wird gerendert.
 *
 * Produkt-Index (Vokabular + Compound-Map) wird erst geladen, wenn ein
 * Fallback ihn wirklich braucht. Danach bleibt er für die gesamte
 * Page-Lifetime im Memory.
 */

// --- PV: Fuzzy-Helpers ---------------------------------------------------

const PV_GERMAN_TRANSLITERATIONS = [
  [/ue/g, 'ü'],
  [/oe/g, 'ö'],
  [/ae/g, 'ä'],
  [/Ue/g, 'Ü'],
  [/Oe/g, 'Ö'],
  [/Ae/g, 'Ä'],
  [/ss/g, 'ß'],
];

function pvGetGermanVariant(query) {
  let variant = query;
  for (const [pattern, replacement] of PV_GERMAN_TRANSLITERATIONS) {
    variant = variant.replace(pattern, replacement);
  }
  return variant !== query ? variant : null;
}

function pvNormalize(s) {
  return (s || '')
    .toLowerCase()
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ß/g, 'ss');
}

// --- PV: Smart-Query (2026-07-12) --------------------------------------------
// Shopify zerlegt deutsche Komposita serverseitig ("konferenztisch" ->
// konferenz OR tisch, inkl. Beschreibungstexte) und liefert dadurch massiv
// Noise (gemessen: konferenztisch 368 Treffer, exakte Phrase 10). Die exakte
// Phrase-Suche ("wort") matcht dank Index-Dekomposition trotzdem Komposita
// ("stuhl" findet Bürostühle: 93). Schwachpunkt der Phrase-Suche ist der
// Plural ("stühle" nur 5, Titel sind Singular) -> deshalb werden vor dem
// Submit mehrere Kandidaten (Phrase, Singular-Formen, Umlaut-Varianten,
// kuratierte Synonyme) via suggest.json gegengeprüft und der trefferstärkste
// gewählt. Liefert kein Kandidat Treffer, bleibt die Original-Query samt
// Shopify-Typo-Toleranz unangetastet.
//
// v4 (2026-07-15, Beda — «se motion» fand den Stuhl «se:motion» nicht, sondern
// lieferte «se ODER motion» = 288 Rauschen): Recherche (Shopify-Docs +
// Algolia/Elastic, adversarisch gegengeprüft) belegt: Shopify indexiert
// Marken-Modell-Namen mit Doppelpunkt («se:motion») als EIN Token — der
// Doppelpunkt ist storefront-seitig KEINE Wortgrenze (unabhängig auf
// primovero.ch reproduziert: «motion» -> nur die Schreibtische, «semotion» -> 0,
// «se:motion» -> 3). Getippt «se motion» matcht es weder im Dropdown (predictive:
// AND + Prefix nur letztes Wort -> 0) noch präzise im Volltext (Query-Relaxation
// -> OR-Explosion 288). Fix = Query-Konkatenation (Standard-IR: Algolia macht das
// nativ, Elastic via word_delimiter_graph+preserve_original): benachbarte
// Query-Wörter mit den Trennern zusammenfügen, die der Katalog wirklich nutzt,
// und JEDEN Kandidaten exakt gegen das Live-Vokabular prüfen
// (pvJoinedCompoundCandidates). Greift im Dropdown (neue Stufe 2.6), im Submit
// (Kandidat; Doppelpunkt-Token wird als exakte Phrase gequotet, damit der
// Volltext es nicht wieder zerlegt) und im /search-Wächter. Kein Wortlisten-
// Pflege (Shopify-Synonyme scheiden aus: hand-gepflegt, max 20/Gruppe, 1000/Shop,
// global eindeutig), generalisiert auf jedes künftige «marke:modell»-Produkt. Der
// 2-Zeichen-Atom-Floor bleibt (nie «se» allein expandieren) — geprüft wird stets
// der ZUSAMMENGEFÜGTE String, nie die Teile.
//
// v2 (2026-07-14, Beda R26 — «two way» fand TwoWay nicht, «trennwand
// schreibtisch» nur 2 unpassende Treffer): Mehrwort-Queries erhalten
// Zusammenschreibungs-Kandidaten («two way» -> «twoway», gedreht
// «schreibtischtrennwand»), Singular je Wort, und Phrase-Synonyme (ganze
// Eingabe als Zeile im Theme-Setting). Trägt kein exakter Kandidat (<3
// Treffer), läuft eine RELAXATIONS-Stufe: Kompositum-Kern-Varianten
// («schreibtisch» -> «tisch», unquoted = nutzt Shopifys Dekomposition,
// gemessen: «trennwand tisch» top = Auftisch-Stellwände) und als Auffangnetz
// quoted-OR («"trennwand" OR "schreibtisch"» = 82 statt 2, beide-Wörter-
// Treffer ranken oben). Gewinnt die unveränderte Roh-Query die Relaxation,
// bleibt das Original. Einwort: zusätzlich Bindestrich-Zusammenzug
// («two-way» -> «twoway») und Compound-Expansion wie im Dropdown.
//
// v3 (2026-07-14, Beda — «Leuchten» 0 Treffer, «Leuchte» 31): Recherche belegt,
// dass Shopifys Storefront-Suche für Deutsch NUR Tippfehler-Toleranz kann —
// kein Stemming, keine Plural/Singular-Gleichsetzung (Stemming nur EN/JA), keine
// Auto-Synonyme, kein Kompositum-Splitting. Gemessen: «leuchten» 0/5 vs «leuchte»
// 31; «schränke» 1 vs «schrank» 9; «stühle» 21 vs «stuhl» 93 — der Singular ist
// über ALLE Warengruppen die trefferstärkere Form. Konsequenz: (1) Der Dropdown-
// Fallback bekommt einen Morphologie-Schritt (pvMorphQueries) — vorher fehlte er,
// darum «Leuchten» = 0 im Dropdown. (2) Der Submit-Pfad bekommt ein
// deterministisches Morphologie-Auffangnetz, falls das schwache suggest-Orakel
// (misst «leuchte» mit 3 statt echten 31) schweigt. Prinzip: lockere Sprachregeln
// erzeugen Kandidaten, der LIVE-Katalog validiert — generalisiert automatisch auf
// neue Kategorien/Produkte, ohne gepflegte Wortliste.

const PV_RAW_QUERY_RE = /["*:]|\b(AND|OR|NOT)\b|(^|\s)-/;

function pvIsRawQuery(q) {
  if (!q) return false;
  const t = q.trim();
  return t.length >= 2 && t.length <= 60 && !PV_RAW_QUERY_RE.test(t);
}

function pvQuotePhrase(q) {
  return q
    .trim()
    .split(/\s+/)
    .map((w) => '"' + w + '"')
    .join(' ');
}

// Letzten Umlaut ins Grundgraphem zurückführen (Plural-Umlaut):
// "bürostühl" -> "bürostuhl" (das ü in "büro" bleibt unangetastet).
function pvUnumlautLast(w) {
  const map = { ä: 'a', ö: 'o', ü: 'u' };
  for (let i = w.length - 1; i >= 0; i--) {
    if (map[w[i]]) return w.slice(0, i) + map[w[i]] + w.slice(i + 1);
  }
  return null;
}

// Deutsche Singular-Heuristik: Plural-Endungen strippen (+ Umlaut-Rückführung).
// Falsche Kandidaten ("leucht") sind harmlos - sie liefern 0 Live-Treffer.
// v3: Regeln von "wenig abschneiden" (natürlichster Singular) zu "viel", damit
// die richtige Singularform ("leuchte", nicht "leucht") vorn steht. Innerhalb
// jeder Regel zuerst die Umlaut-Rückführung (schränk->schrank, stühl->stuhl,
// büch->buch), dann die rohe Basis.
function pvSingularCandidates(word) {
  const w = (word || '').toLowerCase();
  const out = [];
  const seen = new Set([w]);
  const push = (v) => {
    if (v && v.length >= 3 && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  };
  for (const suf of ['s', 'n', 'e', 'en', 'er']) {
    if (w.endsWith(suf) && w.length - suf.length >= 3) {
      const base = w.slice(0, w.length - suf.length);
      push(pvUnumlautLast(base));
      push(base);
    }
  }
  return out.slice(0, 5);
}

// Grundform-Query-Kandidaten für Dropdown UND Submit. Im Deutschen steht der
// Kopf eines Kompositums/einer Phrase HINTEN ("grosse Tische" -> Kopf "Tische"),
// darum ist die Singularform des letzten Wortes besonders wertvoll.
function pvMorphQueries(query) {
  const words = (query || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const cands = [];
  const add = (s) => {
    const t = (s || '').trim();
    if (t && cands.indexOf(t) === -1) cands.push(t);
  };
  const baseOf = (w) => pvSingularCandidates(w)[0] || w.toLowerCase();
  if (words.length === 1) {
    for (const b of pvSingularCandidates(words[0])) add(b);
  } else {
    add(words.map(baseOf).join(' ')); // alle Wörter singular
    add(words.slice(0, -1).concat([baseOf(words[words.length - 1])]).join(' ')); // nur Kopf singular
  }
  const low = query.toLowerCase().trim();
  return cands.filter((c) => c !== low).slice(0, 3);
}

// Erstbuchstabe gross — nur für die Banner-Anzeige deutscher Nomen.
function pvTitle(s) {
  if (!s) return s;
  // Marken-/Compound-Token (mit Trenner, z.B. «se:motion») nicht kapitalisieren.
  if (/[:._\/\-]/.test(s)) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function pvParseSynonyms(raw) {
  const map = new Map();
  if (!raw) return map;
  for (const line of String(raw).split(/\r?\n/)) {
    const m = line.split(/=>|=/);
    if (m.length < 2) continue;
    const from = m[0].trim().toLowerCase();
    const to = m[m.length - 1].trim();
    if (from && to && from !== to.toLowerCase()) map.set(from, to);
  }
  return map;
}

function pvApplySynonyms(query, synMap) {
  if (!synMap || synMap.size === 0) return { query: query, changed: false };
  let changed = false;
  const out = query
    .trim()
    .split(/\s+/)
    .map((w) => {
      const r = synMap.get(w.toLowerCase());
      if (r) {
        changed = true;
        return r;
      }
      return w;
    });
  return { query: out.join(' '), changed: changed };
}

const pvCountCache = new Map();

// Produkt-Trefferzahl (0-10, capped) der Predictive-API als Entscheidungsbasis.
async function pvSuggestProductCount(query) {
  if (pvCountCache.has(query)) return pvCountCache.get(query);
  const base =
    (typeof FoxTheme !== 'undefined' && FoxTheme.routes && FoxTheme.routes.predictive_search_url) ||
    '/search/suggest';
  const url =
    base +
    '.json?q=' +
    encodeURIComponent(query) +
    '&resources[type]=product&resources[limit]=10&resources[limit_scope]=each' +
    '&resources[options][unavailable_products]=last' +
    '&resources[options][fields]=title,product_type,variants.title,vendor,tag,variants.sku';
  try {
    const res = await fetch(url.replace('.json.json', '.json'));
    if (!res.ok) return 0;
    const d = await res.json();
    const n =
      (d && d.resources && d.resources.results && d.resources.results.products
        ? d.resources.results.products.length
        : 0) || 0;
    pvCountCache.set(query, n);
    return n;
  } catch (e) {
    return 0;
  }
}

// --- PV: Ausgangs-Wächter (2026-08-21) --------------------------------------
// Befund des Such-Audits vom 21.08.: Die Kandidatenwahl stützt sich auf
// suggest.json — eine ANDERE Engine als die /search-Seite. Sie ist bei 10
// gedeckelt und liest keine Beschreibungstexte, misst also nicht das, was der
// Kunde am Ende sieht (gemessen: «leuchten» Seite 113 / Orakel 0). Daraus
// entstanden zwei Fehlerbilder, beide live nachgewiesen:
//   1. Erfundene Singularformen wurden UNGEPRÜFT abgeschickt und machten aus
//      Treffern eine Leerseite («sitzungszimmer» 300 -> «sitzungszimm» 0,
//      «wandpanele» 23 -> «wandpanel» 0).
//   2. Das quoted-OR-Auffangnetz verbreiterte statt zu präzisieren
//      («schreibtisch weiss» 53 -> 216).
// Statt die bewährte Kandidatenlogik umzubauen (sie liefert gemessene 99 %
// Precision@10), prüft ein Wächter am AUSGANG jede Umschreibung gegen die
// richtige Quelle: die Trefferzahl der echten Suchseite.
//
// Fällt die Messung aus (Template fehlt, Timeout, Netzfehler), verhält sich
// alles exakt wie vorher — der Wächter kann nur zurücknehmen, nie verschlimmern.

// Schwellen bewusst konservativ: nur eingreifen, wenn die Umschreibung
// nachweislich schadet.
var PV_WAECHTER_TIMEOUT_MS = 1400;
var PV_WAECHTER_VERBREITERUNG = 1.5; // ab dem 1.5-fachen gilt es als Verwässerung
// Verbreiterung nur zurücknehmen, wenn das Original schon eine brauchbare Menge
// liefert. Bei 1–9 Treffern ist Aufmachen erwünscht («mr 132»: 1 -> 3 exakte).
var PV_WAECHTER_MIN_BASIS = 10;

// Echte Trefferzahl der /search-Seite über das schlanke Zähl-Template.
// Antwort ist eine reine Zahl (wenige Bytes). Fehlt das Template, liefert
// Shopify die normale HTML-Seite — dann greift der Längen-Riegel und wir
// geben null zurück («nicht messbar»).
var pvZaehlCache = new Map();
async function pvEchteTrefferzahl(query) {
  if (!query) return null;
  if (pvZaehlCache.has(query)) return pvZaehlCache.get(query);
  try {
    const root = (typeof FoxTheme !== 'undefined' && FoxTheme.routes && FoxTheme.routes.root_url) || '/';
    const base = root.endsWith('/') ? root.slice(0, -1) : root;
    const res = await fetch(
      `${base}/search?q=${encodeURIComponent(query)}&type=product&view=pvcount`
    );
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    if (text.length > 12) return null; // keine nackte Zahl -> Template fehlt
    const n = parseInt(text, 10);
    if (!Number.isFinite(n)) return null;
    pvZaehlCache.set(query, n);
    return n;
  } catch (e) {
    return null;
  }
}

// Prüft eine fertige Entscheidung, bevor sie abgeschickt wird.
// Rückgabe: die Entscheidung (unverändert) oder null = Original behalten.
async function pvPruefeEntscheid(original, entscheid) {
  if (!entscheid || !entscheid.query) return entscheid;
  if (entscheid.query === original) return entscheid;

  // Der Wächter läuft NUR, wenn tatsächlich Wörter verändert wurden.
  //
  // Wurden bloss Anführungszeichen gesetzt (gleiche Wörter, gleiche Reihenfolge,
  // z. B. «bürostuhl» -> «"bürostuhl"»), ist das die Kernfunktion des Systems —
  // gemessen 98,6 % Precision@10 — und kann nur ENGER werden, nie leer laufen.
  // Diese Fälle sind die grosse Mehrheit; sie hier durchzuwinken hält die Suche
  // schnell. Gemessen: mit Prüfung bei jedem Fall stieg die Wartezeit im Median
  // von 153 auf 316 ms und bei «bürostuhl» auf 1600 ms — für null Nutzen.
  //
  // Riskant sind genau die Fälle, in denen die Heuristik Wörter ERSETZT
  // (Singular, Synonym, Compound) oder mit ODER verknüpft. Nur dort wird geprüft.
  const nurGequotet =
    entscheid.display && entscheid.display.toLowerCase().trim() === String(original).toLowerCase().trim();
  if (nurGequotet) return entscheid;

  const messung = await Promise.race([
    Promise.all([pvEchteTrefferzahl(entscheid.query), pvEchteTrefferzahl(original)]),
    new Promise((resolve) => setTimeout(() => resolve(null), PV_WAECHTER_TIMEOUT_MS)),
  ]);
  if (!messung) return entscheid; // Timeout -> lieber wie bisher als warten
  let [neu, alt] = messung;
  if (neu === null) return entscheid; // nicht messbar

  // 1. Die Umschreibung führt in die Leere. Nur zurücknehmen, wenn das Original
  //    tatsächlich etwas gefunden hätte.
  if (neu === 0) {
    if (alt === null) alt = await pvEchteTrefferzahl(original);
    return alt !== null && alt > 0 ? null : entscheid;
  }
  if (alt === null) return entscheid;

  // 2. Die Umschreibung verbreitert, statt zu präzisieren.
  if (alt >= PV_WAECHTER_MIN_BASIS && neu > alt * PV_WAECHTER_VERBREITERUNG) return null;

  return entscheid;
}

// Kompositum-Kern: längstes bekanntes Schluss-Atom eines langen Wortes
// («schreibtisch» -> «tisch») — nur Atome, die im Produkt-Vokabular existieren,
// damit keine Fantasie-Kerne entstehen.
function pvCompoundTail(word, vocab) {
  const norm = pvNormalize(word);
  if (!vocab || norm.length < 8) return null;
  for (let len = norm.length - 3; len >= 4; len--) {
    const suffix = norm.slice(norm.length - len);
    if (vocab.has(suffix)) {
      return String(vocab.get(suffix) || suffix).toLowerCase();
    }
  }
  return null;
}

// Kandidaten bauen, parallel zählen, besten wählen.
// Rückgabe null = Original unverändert benutzen.
//
// PV 2026-08-21: Diese Funktion entscheidet weiter genau wie bisher (sie liefert
// gemessene 99 % Precision@10). Neu läuft ihr Ergebnis durch pvPruefeEntscheid,
// bevor es abgeschickt wird — siehe pvDecideSmartQuery weiter unten.
async function pvDecideSmartQueryRoh(original, synMap) {
  const raw = (original || '').trim();
  // v4: Ein einzelnes Doppelpunkt-Token («se:motion» — direkt getippt oder aus der
  // Dropdown-Rekonstruktion übernommen) ist ein Marken-Compound. Als exakte Phrase
  // quoten, damit der Volltext es nicht wieder in ein OR zerlegt. Der Doppelpunkt
  // ist storefront-seitig literal (kein Feld-Operator); quoted gemessen = 3 exakte
  // Treffer. Nur bei Doppelpunkt (eindeutiger Marken-Trenner) — Bindestrich-Wörter
  // wie «two-way» laufen bewusst durch die normale Kandidatenlogik.
  if (raw && raw.indexOf(':') !== -1 && !/\s/.test(raw) && raw.length <= 40) {
    return { query: pvQuotePhrase(raw), display: raw, changed: false };
  }
  if (!pvIsRawQuery(raw)) return null;

  const applied = pvApplySynonyms(raw, synMap);
  let base = applied.query.trim();
  // Phrase-Synonym (ganze Eingabe als Setting-Zeile, z.B.
  // «trennwand schreibtisch => tischtrennwand») schlägt Wort-Synonyme.
  if (synMap && synMap.size) {
    const phrase = synMap.get(raw.toLowerCase().replace(/\s+/g, ' '));
    if (phrase) base = phrase.trim();
  }
  const words = base.split(/\s+/);

  // Vokabular parallel anwärmen — der Submit wartet höchstens kurz darauf
  // (braucht es nur die Relaxations-Stufe; beim Kaltstart fällt sie weg).
  const vocabPromise = Promise.race([
    pvLoadVocabulary().catch(() => null),
    new Promise((resolve) => setTimeout(() => resolve(null), 2500)),
  ]);

  const seen = new Set();
  const cands = [];
  const addCand = (plain) => {
    const key = (plain || '').toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    cands.push({ plain: plain, quoted: pvQuotePhrase(plain) });
  };

  addCand(base);
  const translit = pvGetGermanVariant(base);
  if (translit) addCand(translit);
  // v4: Compound-Rekonstruktion als früher, hochwertiger Kandidat
  // («se motion» -> «se:motion»). Live-Vokabular validiert; die Zählung unten
  // wählt die trefferstärkste Form. Nie «se» allein — nur der zusammengefügte
  // String, den das Vokabular als echtes Katalog-Token bestätigt.
  if (words.length >= 2) {
    try {
      for (const j of await pvJoinedCompoundCandidates(base)) addCand(j);
    } catch (e) {
      // Vokabular nicht verfügbar -> Rekonstruktion entfällt
    }
  }
  if (words.length === 1) {
    if (base.indexOf('-') !== -1) addCand(base.replace(/-+/g, ''));
    for (const s of pvSingularCandidates(base)) addCand(s);
    if (translit) for (const s of pvSingularCandidates(translit)) addCand(s);
  }
  if (words.length >= 2 && words.length <= 3) {
    addCand(words.join(''));
    if (words.length === 2) addCand(words[1] + words[0]);
    addCand(words.map((w) => pvSingularCandidates(w)[0] || w).join(' '));
  }
  try {
    const expanded = await pvExpandToCompound(base);
    if (expanded) addCand(expanded);
  } catch (e) {
    // Index nicht verfügbar -> Kandidat entfällt
  }

  const capped = cands.slice(0, 8);
  const counts = await Promise.race([
    Promise.all(capped.map((c) => pvSuggestProductCount(c.quoted))),
    new Promise((resolve) => setTimeout(() => resolve(null), 1500)),
  ]);
  if (!counts) return null; // Timeout -> lieber Original als warten

  let best = null;
  let bestN = 0;
  capped.forEach((c, i) => {
    if (counts[i] > bestN) {
      best = c;
      bestN = counts[i];
    }
  });

  // RELAXATION (nur 2-3 Wörter, wenn kein exakter Kandidat trägt): erst die
  // Roh-Query selbst (gewinnt sie, bleibt alles beim Original), dann
  // Kompositum-Kern-Varianten (unquoted), zum Schluss das quoted-OR-Netz.
  if (bestN < 3 && words.length >= 2 && words.length <= 3) {
    const relaxed = [{ plain: base, query: base, isBase: true }];
    const vocab = await vocabPromise;
    if (vocab) {
      words.forEach((w, i) => {
        const tail = pvCompoundTail(w, vocab);
        if (tail && tail !== w.toLowerCase()) {
          const v = words.slice();
          v[i] = tail;
          relaxed.push({ plain: v.join(' '), query: v.join(' ') });
        }
      });
    }
    relaxed.push({
      plain: words.join(' oder '),
      query: words.map((w) => '"' + w + '"').join(' OR '),
    });

    const relaxedCounts = await Promise.race([
      Promise.all(relaxed.map((c) => pvSuggestProductCount(c.query))),
      new Promise((resolve) => setTimeout(() => resolve(null), 1500)),
    ]);
    if (relaxedCounts) {
      // Auswahlregel: Intent vor Breite. Die Suggest-API sieht nur
      // Titel/Tags/SKU — Kern-Varianten mit >=2 Suggest-Treffern sind darum
      // präzise Produkttreffer und schlagen das breite OR-Netz. Das Original
      // gewinnt nur, wenn es ALLE Alternativen übertrifft.
      const baseN = relaxedCounts[0];
      const othersMax = Math.max.apply(null, relaxedCounts.slice(1).concat([0]));
      if (baseN > othersMax && baseN > bestN) return null; // Original ist am stärksten
      for (let i = 1; i < relaxed.length - 1; i++) {
        if (relaxedCounts[i] >= 2) {
          return {
            query: relaxed[i].query,
            display: relaxed[i].plain,
            changed: relaxed[i].plain.toLowerCase() !== raw.toLowerCase(),
          };
        }
      }
      const orIdx = relaxed.length - 1;
      if (relaxedCounts[orIdx] > bestN && relaxedCounts[orIdx] > baseN) {
        return {
          query: relaxed[orIdx].query,
          display: relaxed[orIdx].plain,
          changed: true,
        };
      }
    }
  }

  // Deterministisches Morphologie-Auffangnetz (v3): Trägt kein Kandidat im
  // schwachen suggest-Orakel (misst «leuchte» mit 3 statt echten 31) UND hat die
  // Roh-Query selbst 0 Treffer, es existiert aber eine klare Singular-Grundform,
  // dann diese trotzdem quoten. Der deutsche Plural gibt systematisch weniger
  // her; das «Trotzdem nach X»-Banner bleibt der Notausgang. Greift v.a. für
  // Einwort-Queries (dort läuft die Relaxations-Stufe oben nicht).
  if (!best || bestN === 0) {
    const baseIdx = capped.findIndex((c) => c.plain.toLowerCase() === base.toLowerCase());
    const baseCount = baseIdx >= 0 ? counts[baseIdx] || 0 : 0;
    if (baseCount === 0) {
      const morph = pvMorphQueries(base)[0];
      if (morph && morph !== base.toLowerCase()) {
        return {
          query: pvQuotePhrase(morph),
          display: morph,
          changed: morph.toLowerCase() !== raw.toLowerCase(),
        };
      }
    }
    return null;
  }

  return {
    query: best.quoted,
    display: best.plain,
    changed: best.plain.toLowerCase() !== raw.toLowerCase(),
  };
}

// PV 2026-08-21: Der öffentliche Einstieg. Entscheidet wie bisher und lässt das
// Ergebnis anschliessend durch den Wächter laufen, der es gegen die echte
// Suchseite gegenprüft. Signatur unverändert — onFormSubmit und der
// /search-Wächter rufen weiterhin diese Funktion auf.
async function pvDecideSmartQuery(original, synMap) {
  const entscheid = await pvDecideSmartQueryRoh(original, synMap);
  try {
    return await pvPruefeEntscheid(original, entscheid);
  } catch (e) {
    return entscheid; // Wächter darf nie die Suche blockieren
  }
}

function pvDamerauLevenshtein(a, b, maxEdits) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  if (Math.abs(a.length - b.length) > maxEdits) return maxEdits + 1;

  const m = a.length;
  const n = b.length;
  let prevPrev = null;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);

  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    let rowMin = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        curr[j] = Math.min(curr[j], prevPrev[j - 2] + 1);
      }
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > maxEdits) return maxEdits + 1;
    prevPrev = prev;
    prev = curr;
    curr = new Array(n + 1);
  }
  return prev[n];
}

// Produkt-Index, einmalig lazy geladen.
// - vocab: normalisierter Token -> Originalschreibweise (für Spellcorrect)
// - compounds: normalisierter Atom-Token -> Set der Originale die ihn als Teil enthalten
//   (z.B. "hive" -> {"se:hive"}, damit eine Suche nach "hive" mit dem vollen Compound an Shopify retried werden kann)
let pvIndexPromise = null;

const PV_COMPOUND_SPLIT_RE = /[:_\/.\-]+/;
const PV_KEEP_CHARS_RE = /[^\p{L}\p{N}:_\/.\-]/gu;

function pvLoadIndex() {
  if (pvIndexPromise) return pvIndexPromise;

  pvIndexPromise = (async () => {
    const root = (FoxTheme && FoxTheme.routes && FoxTheme.routes.root_url) || '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    const vocab = new Map();
    const compounds = new Map();

    const addVocabEntry = (cleaned) => {
      if (!cleaned || cleaned.length < 3) return;
      const norm = pvNormalize(cleaned);
      if (!vocab.has(norm)) vocab.set(norm, cleaned);
    };

    const addCompoundLink = (atom, compoundOriginal) => {
      const norm = pvNormalize(atom);
      let set = compounds.get(norm);
      if (!set) {
        set = new Set();
        compounds.set(norm, set);
      }
      set.add(compoundOriginal);
    };

    for (let page = 1; page <= 20; page++) {
      try {
        const res = await fetch(`${base}products.json?limit=250&page=${page}`);
        if (!res.ok) break;
        const data = await res.json();
        const products = (data && data.products) || [];
        if (products.length === 0) break;

        for (const p of products) {
          const tagString = Array.isArray(p.tags) ? p.tags.join(' ') : p.tags || '';
          // Hidden konfigurierte Produkte raushalten
          const tagList = tagString.toLowerCase().split(/[,\s]+/);
          if (tagList.includes('configured')) continue;

          const text = [p.title, p.vendor, p.product_type, tagString].filter(Boolean).join(' ');
          for (const raw of text.split(/\s+/)) {
            const cleaned = raw.replace(PV_KEEP_CHARS_RE, '');
            if (cleaned.length < 2) continue;

            // 1) Den ganzen "Wortbrocken" (inkl. Compound-Trenner wie ":" oder "-") als Vokabel.
            addVocabEntry(cleaned);

            // 2) Wenn das Token Compound-Trenner enthält: Atome separat indexieren UND
            //    die Atom->Compound-Beziehung in der compounds-Map merken.
            if (PV_COMPOUND_SPLIT_RE.test(cleaned)) {
              const atoms = cleaned.split(PV_COMPOUND_SPLIT_RE).filter((a) => a.length >= 3);
              for (const atom of atoms) {
                addVocabEntry(atom);
                addCompoundLink(atom, cleaned);
              }
            }
          }
        }

        if (products.length < 250) break;
      } catch (e) {
        break;
      }
    }

    return { vocab, compounds };
  })();

  return pvIndexPromise;
}

async function pvLoadVocabulary() {
  return (await pvLoadIndex()).vocab;
}

async function pvLoadCompounds() {
  return (await pvLoadIndex()).compounds;
}

// Versucht jeden Query-Begriff durch ein bekanntes Compound zu ersetzen, in dem er als Atom vorkommt.
// "hive" -> "se:hive", "ofy" -> "se:ofy", "konferenz" bleibt unverändert (falls als Vollwort vorhanden).
// Gibt null zurück wenn keine Ersetzung gemacht wurde.
async function pvExpandToCompound(query) {
  const compounds = await pvLoadCompounds();
  if (compounds.size === 0) return null;

  const words = query.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;

  let expanded = false;
  const result = words.map((w) => {
    const norm = pvNormalize(w);
    if (norm.length < 3) return w;
    const set = compounds.get(norm);
    if (!set || set.size === 0) return w;
    // Erstes bekanntes Compound zurückgeben (deterministisch via Set-Insertion-Order).
    expanded = true;
    return set.values().next().value;
  });

  return expanded ? result.join(' ') : null;
}

// v4: Compound-Token-REKONSTRUKTION (Gegenstück zu pvExpandToCompound). Shopify
// indexiert «se:motion» als EIN Doppelpunkt-Token; getippt «se motion»
// (Leerzeichen) erreicht es nie. Standard-IR-Query-Konkatenation: benachbarte
// Query-Wörter mit den Trennern zusammenfügen, die der Katalog wirklich nutzt,
// und JEDEN Kandidaten EXAKT gegen das Live-Vokabular prüfen. Kein Wortlisten-
// Pflege; existiert das Token nicht, fällt der Kandidat durch. Geprüft wird stets
// der zusammengefügte String (nie «se» allein -> der 2-Zeichen-Atom-Floor bleibt
// unangetastet, denn das volle Token «se:motion» IST im Vokabular).
const PV_JOIN_SEPS = [':', '-', '.', '/', '_', ''];

async function pvJoinedCompoundCandidates(query) {
  const words = (query || '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return [];
  let vocab;
  try {
    vocab = await pvLoadVocabulary();
  } catch (e) {
    return [];
  }
  if (!vocab || !vocab.size) return [];

  const results = [];
  const seen = new Set([(query || '').toLowerCase().trim()]);
  const push = (phrase) => {
    const key = (phrase || '').toLowerCase().trim();
    if (key && !seen.has(key)) {
      seen.add(key);
      results.push(phrase);
    }
  };

  // Zwei benachbarte Wörter zu einem echten Katalog-Token verschmelzen (Trenner
  // in Reihenfolge: Doppelpunkt/Bindestrich zuerst, leerer Join zuletzt).
  const joinHit = (a, b) => {
    for (const sep of PV_JOIN_SEPS) {
      const norm = pvNormalize(a + sep + b);
      if (vocab.has(norm)) return vocab.get(norm); // Originalschreibweise des Tokens
    }
    return null;
  };

  // Greedy links->rechts: «se motion net» -> «se:motion net» (erstes Paar ist
  // echtes Token, «net» bleibt eigenständiges Wort).
  const merged = [];
  let i = 0;
  let didMerge = false;
  while (i < words.length) {
    const hit = i + 1 < words.length ? joinHit(words[i], words[i + 1]) : null;
    if (hit) {
      merged.push(hit);
      i += 2;
      didMerge = true;
    } else {
      merged.push(words[i]);
      i += 1;
    }
  }
  if (didMerge) push(merged.join(' '));

  // Genau 2 Wörter: zusätzlich jede einzelne Trenner-Variante als eigener
  // Kandidat (die Live-Zählung im Submit wählt die trefferstärkste Form).
  if (words.length === 2) {
    for (const sep of PV_JOIN_SEPS) {
      const norm = pvNormalize(words[0] + sep + words[1]);
      if (vocab.has(norm)) push(vocab.get(norm));
    }
  }

  return results.slice(0, 4);
}

async function pvSpellCorrectQuery(query) {
  const vocab = await pvLoadVocabulary();
  if (vocab.size === 0) return null;

  const words = query.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;

  let corrected = false;
  const fixed = words.map((w) => {
    const normW = pvNormalize(w);
    if (normW.length < 3) return w;
    if (vocab.has(normW)) return vocab.get(normW);

    let bestNorm = null;
    let bestDist = Infinity;
    const maxEdits = normW.length <= 4 ? 1 : 2;

    for (const v of vocab.keys()) {
      if (Math.abs(v.length - normW.length) > maxEdits) continue;
      const d = pvDamerauLevenshtein(normW, v, maxEdits);
      if (d < bestDist) {
        bestDist = d;
        bestNorm = v;
        if (d === 0) break;
      }
    }

    if (bestNorm && bestDist <= maxEdits) {
      corrected = true;
      return vocab.get(bestNorm);
    }
    return w;
  });

  return corrected ? fixed.join(' ') : null;
}

function pvResponseHasResults(responseText, sectionId) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(responseText, 'text/html');
    const container = doc.getElementById(`PredictiveSearchResults-${sectionId}`);
    if (!container) return true; // unbekannt -> rendern lassen
    if (container.querySelector('.empty-state')) return false;
    if (!container.innerHTML.trim()) return false;
    // v3: "hat Treffer" = hat PRODUKT-Treffer. Die Predictive-Section rendert
    // auch bei 0 Produkten Nicht-Produkt-Inhalt (Query-/Collection-Vorschläge),
    // hat also kein .empty-state — das galt bisher fälschlich als Treffer und
    // würgte die Morphologie-Fallback-Kette ab (Ursache «Leuchten» = 0 Produkte
    // im Dropdown, obwohl «Leuchte» 31 hat). Produkte stehen ausschliesslich im
    // Produkt-Block; Empfehlungen liegen ausserhalb dieses Containers.
    const hasProducts =
      !!container.querySelector('#predictive-search-results-products-list') ||
      !!container.querySelector('.predictive-search-result--products') ||
      !!container.querySelector('a[href*="/products/"]');
    return hasProducts;
  } catch (e) {
    return true;
  }
}

function pvEscapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function pvBuildCorrectionBannerHtml(correction, options = {}) {
  // options.actionLabel: Text fürs Trotzdem-Element
  // options.actionTag: 'button' (Predictive) oder 'a' (Search-Page)
  // options.actionHref: nur für 'a'
  const actionLabel = options.actionLabel || `Stattdessen nach „${correction.original}“ suchen`;
  const isLink = options.actionTag === 'a';
  const actionEl = isLink
    ? `<a class="pv-search-correction-banner__force-link" href="${pvEscapeHtml(options.actionHref || '#')}">${pvEscapeHtml(actionLabel)}</a>`
    : `<button type="button" class="pv-search-correction-banner__force-link">${pvEscapeHtml(actionLabel)}</button>`;

  return `
    <div class="pv-search-correction-banner" role="status">
      <span class="pv-search-correction-banner__text">
        Ergebnisse für <strong>${pvEscapeHtml(pvTitle(correction.corrected))}</strong>
      </span>
      ${actionEl}
    </div>
  `;
}

// --- Class ----------------------------------------------------------------

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);

    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };

    this._flowId = 0;
    this._lastCorrection = null; // { original, corrected } – nur wenn der zuletzt gerenderte Treffer korrigiert war
    this._skipCorrectionOnce = false;

    // PV: Form-Submit abfangen, damit eine bereits ermittelte Korrektur an die /search-Seite mitgegeben wird
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );

    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      if (this.toggleNavigationButton) {
        this.toggleNavigationButton.classList.remove('is-show');
      }
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }

    this.classList.add('predictive-search-open');

    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }

    this.searchWithFallback(this.getQuery());
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange() {
    const selectedUrl = this.searchProductTypes?.value;

    if (selectedUrl) {
      window.location.href = selectedUrl;
      return;
    }

    const query = this.getQuery();
    if (query.length > 0) {
      this.searchWithFallback(query);
    }
  }

  setupURL() {
    return this.setupURLForQuery(this.getQuery());
  }

  setupURLForQuery(query) {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    url.searchParams.set('q', query);

    // PV: Nur relevante Ressourcentypen abfragen. v5 (2026-07-15): 'query' raus —
    // die «Nach X suchen»-Query-Vorschlagszeilen sind Declutter-Noise (Produkte
    // werden bereits live gezeigt). v6 (2026-07-26): 'page' zurück — gemessen lieferten
    // architekten/konfigurator/lieferung/kontakt/faq vorher 0 Treffer. Rendering ist auf
    // max. 3 Zeilen in einer eigenen Gruppe «Beratung & Service» begrenzt.
    url.searchParams.set('resources[type]', 'product,collection,page');

    // PV: Anzahl Resultate
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 6);
    url.searchParams.set('resources[limit_scope]', 'each');

    // PV: Nicht verfügbare Produkte nicht komplett ausblenden, sondern hinten anzeigen
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    // PV: Suchfelder erweitern – wichtig: tag ergänzen
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,tag,variants.sku'
    );

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));

    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    this.searchWithFallback(this.getQuery());
  }

  // PV: 4-stufige Fallback-Kette für Typo-Toleranz
  async searchWithFallback(originalQuery) {
    this._flowId += 1;
    const myFlowId = this._flowId;
    const stillCurrent = () => myFlowId === this._flowId && this.getQuery() === originalQuery;

    // Wenn der User explizit "Trotzdem suchen" geklickt hat, Korrektur einmalig überspringen
    const skipCorrection = this._skipCorrectionOnce === true;
    this._skipCorrectionOnce = false;

    this.setLoadingState(true);

    try {
      // 1. Originale Query
      let result = await this.fetchQueryResponse(originalQuery);
      if (!stillCurrent()) return;
      if (result.hasResults || skipCorrection) {
        this._lastCorrection = null;
        this.renderResponseText(result.text);
        return;
      }

      // 2. Deutsche Transliteration (ue->ü etc.)
      const germanVariant = pvGetGermanVariant(originalQuery);
      if (germanVariant && stillCurrent()) {
        result = await this.fetchQueryResponse(germanVariant);
        if (!stillCurrent()) return;
        if (result.hasResults) {
          this._lastCorrection = { original: originalQuery, corrected: germanVariant };
          this.renderResponseText(result.text, this._lastCorrection);
          return;
        }
      }

      // 2.5 Morphologie: Plural -> Singular (+ Umlaut-Rückführung). Shopifys
      // DE-Suche macht KEIN Stemming, «Leuchten» matcht sonst nichts, obwohl
      // «Leuchte» 31 Treffer hat. Generische Sprachregel -> greift auch bei
      // neuen Warengruppen. Lose Kandidaten sind ok: der Live-Index entscheidet,
      // Treffer-lose Formen fallen einfach durch. Max. 3 Zusatz-Requests, und nur
      // wenn Original + Transliteration schon 0 Treffer hatten.
      for (const cand of pvMorphQueries(originalQuery)) {
        if (!stillCurrent()) return;
        result = await this.fetchQueryResponse(cand);
        if (!stillCurrent()) return;
        if (result.hasResults) {
          this._lastCorrection = { original: originalQuery, corrected: cand };
          this.renderResponseText(result.text, this._lastCorrection);
          return;
        }
      }

      // 2.6 Compound-Token-Rekonstruktion: «se motion» -> «se:motion». Marken-
      // Modell-Namen mit Doppelpunkt (Sedus se:-Linie) sind EIN Katalog-Token;
      // getippte Leerzeichen dazwischen via echtem Vokabular-Token zusammenführen.
      // Läuft nur, nachdem Original + Transliteration + Morphologie 0 Produkt-
      // Treffer hatten (Guard: nur bei Null-Treffer korrigieren). Standard-IR-
      // Query-Konkatenation, gegen den Live-Katalog validiert.
      try {
        for (const cand of await pvJoinedCompoundCandidates(originalQuery)) {
          if (!stillCurrent()) return;
          result = await this.fetchQueryResponse(cand);
          if (!stillCurrent()) return;
          if (result.hasResults) {
            this._lastCorrection = { original: originalQuery, corrected: cand };
            this.renderResponseText(result.text, this._lastCorrection);
            return;
          }
        }
      } catch (e) {
        // Vokabular-Load fehlgeschlagen -> Schritt überspringen
      }

      // 3. Compound-Expansion ("hive" -> "se:hive", "ofy" -> "se:ofy", ...)
      // Hilft bei Shopify-Tokenizer-Schwäche: das Atom existiert im Produktindex,
      // aber Shopify findet es nur über die volle Compound-Form.
      try {
        const expanded = await pvExpandToCompound(originalQuery);
        if (expanded && expanded !== originalQuery && stillCurrent()) {
          result = await this.fetchQueryResponse(expanded);
          if (!stillCurrent()) return;
          if (result.hasResults) {
            this._lastCorrection = { original: originalQuery, corrected: expanded };
            this.renderResponseText(result.text, this._lastCorrection);
            return;
          }
        }
      } catch (e) {
        // Index-Load fehlgeschlagen -> Schritt überspringen
      }

      // 4. Fuzzy-Spellcorrection gegen Produkt-Vokabular
      try {
        const corrected = await pvSpellCorrectQuery(originalQuery);
        if (corrected && corrected !== originalQuery && stillCurrent()) {
          result = await this.fetchQueryResponse(corrected);
          if (!stillCurrent()) return;
          if (result.hasResults) {
            this._lastCorrection = { original: originalQuery, corrected };
            this.renderResponseText(result.text, this._lastCorrection);
            return;
          }
        }
      } catch (e) {
        // Vocab-Load fehlgeschlagen -> Fuzzy-Step überspringen
      }

      // 5. Empty-State des Originals rendern
      if (stillCurrent()) {
        const fallback = await this.fetchQueryResponse(originalQuery);
        if (stillCurrent()) {
          this._lastCorrection = null;
          this.renderResponseText(fallback.text);
        }
      }
    } catch (error) {
      console.error('Error fetching data: ', error);
      this.setAttribute('error', 'Failed to load data');
    } finally {
      if (myFlowId === this._flowId) {
        this.setLoadingState(false);
      }
    }
  }

  async fetchQueryResponse(query) {
    const url = this.setupURLForQuery(query).toString();
    let text;
    if (this.cachedMap.has(url)) {
      text = this.cachedMap.get(url);
    } else {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      text = await response.text();
      this.cachedMap.set(url, text);
    }
    const sectionId = FoxTheme.utils.getSectionId(this);
    return { text, hasResults: pvResponseHasResults(text, sectionId) };
  }

  renderResponseText(responseText, correction = null) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        // PV: "Meinten Sie X?"-Banner einsetzen, wenn die gerenderten Treffer aus einer Korrektur stammen
        if (correction && correction.original && correction.corrected) {
          this.injectCorrectionBanner(targetElement, correction);
        }

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavigationButton?.classList.add('is-show');
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  // PV: Banner über die Trefferliste setzen, mit klickbarem "Trotzdem nach 'X' suchen"-Element
  injectCorrectionBanner(container, correction) {
    if (container.querySelector('.pv-search-correction-banner')) return;

    const wrap = document.createElement('div');
    wrap.innerHTML = pvBuildCorrectionBannerHtml(correction);
    const banner = wrap.firstElementChild;
    if (!banner) return;

    const forceBtn = banner.querySelector('.pv-search-correction-banner__force-link');
    if (forceBtn) {
      forceBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.input.value = correction.original;
        this._skipCorrectionOnce = true;
        this.searchWithFallback(correction.original);
      });
    }

    container.insertBefore(banner, container.firstChild);
  }

  // PV: Form-Submit.
  // Schritt 1 (bestehend): Dropdown-Korrektur der Session an /search weiterreichen.
  // Schritt 2 (Smart-Query): Query vor der Navigation als exakte Phrase quoten und
  // gegen Singular-/Umlaut-/Synonym-Kandidaten abwägen (suggest.json-Counts) –
  // verhindert Shopifys Kompositum-Zerlegungs-Noise (368 statt 10 Treffer).
  onFormSubmit(event) {
    if (this._pvSubmitting) return; // programmatischer Re-Submit

    const typedQuery = this.getQuery();
    let bannerOriginal = null;

    if (
      this._lastCorrection &&
      this._lastCorrection.original === typedQuery &&
      this._lastCorrection.corrected &&
      this._lastCorrection.corrected !== typedQuery
    ) {
      this.input.value = this._lastCorrection.corrected;
      bannerOriginal = this._lastCorrection.original;
    }

    if (this.dataset.pvSmart === 'false') {
      if (bannerOriginal) this._pvSetHiddenOriginal(event.target, bannerOriginal);
      return; // Native Submission läuft weiter
    }

    event.preventDefault();
    const form = event.target;
    const effectiveQuery = this.getQuery(); // ggf. bereits Dropdown-korrigiert

    const finish = (decision) => {
      if (decision && decision.query) {
        this.input.value = decision.query;
        if (decision.changed || bannerOriginal) {
          this._pvSetHiddenOriginal(form, bannerOriginal || typedQuery);
        }
      } else if (bannerOriginal) {
        this._pvSetHiddenOriginal(form, bannerOriginal);
      }
      this._pvSubmitting = true;
      form.submit(); // umgeht Submit-Listener -> kein Loop
    };

    pvDecideSmartQuery(effectiveQuery, this._pvGetSynonyms())
      .then(finish)
      .catch(() => finish(null));
  }

  _pvSetHiddenOriginal(form, value) {
    let hidden = form.querySelector('input[name="pv_corrected_from"]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'pv_corrected_from';
      form.appendChild(hidden);
    }
    hidden.value = value;
  }

  _pvGetSynonyms() {
    if (!this._pvSynonymsMap) {
      this._pvSynonymsMap = pvParseSynonyms(this.dataset.pvSynonyms || '');
    }
    return this._pvSynonymsMap;
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);

    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavigationButton?.classList.toggle('is-show', !isOpen);
    }

    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);

// PV: "Meinten Sie X?"-Banner auf der /search-Ergebnisseite, wenn dort über
// ?pv_corrected_from=… gelandet wurde (kommt von der Predictive-Search-Submit-Korrektur).
// Wenn nicht auf /search oder kein passender Parameter, passiert hier nichts.
(function () {
  function pvInjectSearchPageCorrectionBanner() {
    try {
      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        return;
      }

      const correctedFrom = params.get('pv_corrected_from');
      if (!correctedFrom) return;

      // Anzeige ohne Phrase-Anführungszeichen (Smart-Query quotet intern);
      // das OR der Relaxations-Stufe liest sich als «oder».
      const correctedTo = (params.get('q') || '').replace(/"/g, '').replace(/\s+OR\s+/g, ' oder ');
      if (!correctedTo || correctedTo === correctedFrom) return;

      // Nur auf der Search-Ergebnisseite einblenden
      const header = document.querySelector('.template-search__header');
      if (!header) return;
      if (header.querySelector('.pv-search-correction-banner')) return;

      // Link zurück zur Original-Query; pvfix verhindert, dass der
      // Smart-Query-Wächter die gewollte Original-Suche gleich re-korrigiert.
      const originalUrl = new URL(window.location.href);
      originalUrl.searchParams.delete('pv_corrected_from');
      originalUrl.searchParams.set('q', correctedFrom);
      originalUrl.searchParams.set('pvfix', '1');

      const html = pvBuildCorrectionBannerHtml(
        { original: correctedFrom, corrected: correctedTo },
        {
          actionTag: 'a',
          actionHref: originalUrl.toString(),
          actionLabel: `Stattdessen nach „${correctedFrom}“ suchen`,
        }
      );

      const wrap = document.createElement('div');
      wrap.innerHTML = html;
      const banner = wrap.firstElementChild;
      if (!banner) return;

      header.insertBefore(banner, header.firstChild);
    } catch (e) {
      // Banner ist Bonus – Fehler nie weitergeben
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pvInjectSearchPageCorrectionBanner);
  } else {
    pvInjectSearchPageCorrectionBanner();
  }
})();

// PV: Smart-Query-Wächter für Direkteinstiege auf /search?q=… (Bookmarks,
// geteilte Links, alte URLs), die nicht durchs Suchformular gelaufen sind.
// Gleiche Entscheidungslogik wie der Submit-Interceptor; leitet einmalig auf
// die Phrase-Variante um. Läuft NICHT bei: bereits korrigierten Suchen
// (pvfix/pv_corrected_from), aktiven Filtern/Pagination, Nicht-Roh-Queries
// (Anführungszeichen/Operatoren) und 0-Treffer-Seiten (dort übernimmt die
// bestehende 0-Treffer-Kaskade in main-search.liquid).
(function () {
  function pvSearchPageSmartGuard() {
    try {
      if (!/\/search\b/.test(window.location.pathname)) return;

      const params = new URLSearchParams(window.location.search);
      const q = params.get('q');
      if (!q || params.get('pvfix') || params.get('pv_corrected_from')) return;
      for (const key of params.keys()) {
        if (key.indexOf('filter.') === 0 || key === 'page') return;
      }
      if (!pvIsRawQuery(q)) return;

      const header = document.querySelector('.template-search__header');
      if (!header) return;
      // v2: Auch 0-Treffer-Seiten smart behandeln («two-way» direkt in der
      // URL). Die inline-Kaskade in main-search.liquid (Quote-Strip/Umlaut)
      // läuft zuerst; navigiert sie, ist unser späteres replace() wirkungslos.

      const el = document.querySelector('predictive-search');
      if (el && el.dataset.pvSmart === 'false') return;
      const synMap = pvParseSynonyms(el ? el.dataset.pvSynonyms : '');

      pvDecideSmartQuery(q, synMap).then(function (d) {
        if (!d || !d.query || d.query === q) return;
        params.set('q', d.query);
        params.set('pvfix', '1');
        if (d.changed) params.set('pv_corrected_from', q);
        window.location.replace(window.location.pathname + '?' + params.toString());
      });
    } catch (e) {
      // Wächter ist Bonus – Fehler nie weitergeben
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pvSearchPageSmartGuard);
  } else {
    pvSearchPageSmartGuard();
  }
})();
